// AGC: Tour Selection Modal - Shows when replaying tours from Settings (v6.9.0)
// AGC: Features: List all tours, show completion status, start any tour

import React from 'react'
import { X, Play, CheckCircle2, Circle, Sparkles } from 'lucide-react'
import { usePlayerPreferences } from '../../providers/PlayerPreferencesProvider'
import { useTourGuide } from '../../providers/TourGuideProvider'
import { allTours, type TourId } from '../../config/tourSteps'

// AGC: Click sound effect
const playClickSound = () => {
  try {
    const audio = new Audio('/sounds/click.mp3')
    audio.volume = 0.5 // AGC: Set volume to 50%
    audio.play().catch((error) => {
      // AGC: Silently fail if audio can't play (user interaction required, etc.)
      console.debug('AGC: Could not play click sound:', error)
    })
  } catch (error) {
    // AGC: Silently fail if audio creation fails
    console.debug('AGC: Could not create audio for click sound:', error)
  }
}

interface TourSelectionModalProps {
  isOpen: boolean
  onClose: () => void
}

// AGC: Tour card for selection with completion status
const TourCard: React.FC<{
  id: TourId
  title: string
  description: string
  icon: string
  estimatedTime: string
  isCompleted: boolean
  onStart: () => void
}> = ({ title, description, icon, estimatedTime, isCompleted, onStart }) => (
  <button
    onClick={() => {
      playClickSound()
      onStart()
    }}
    className={`w-full p-4 rounded-xl transition-all group text-left ${
      isCompleted 
        ? 'bg-slate-800/50 hover:bg-slate-700/80 border border-emerald-500/30 hover:border-emerald-400/50' 
        : 'bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 hover:border-cyan-500/50'
    }`}
  >
    <div className="flex items-start gap-3">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform ${
        isCompleted 
          ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20' 
          : 'bg-gradient-to-br from-cyan-500/20 to-purple-500/20'
      }`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className={`font-semibold text-base transition-colors ${
            isCompleted 
              ? 'text-emerald-300 group-hover:text-emerald-200' 
              : 'text-white group-hover:text-cyan-300'
          }`}>
            {title}
          </h4>
          {isCompleted ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          ) : (
            <Circle className="w-4 h-4 text-slate-500 flex-shrink-0" />
          )}
        </div>
        <p className="text-sm text-slate-400 mt-0.5">
          {description}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-slate-500">
            {estimatedTime}
          </span>
          {isCompleted && (
            <span className="text-xs text-emerald-400/80">• Completed</span>
          )}
        </div>
      </div>
      <div className={`p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${
        isCompleted 
          ? 'bg-emerald-500/20' 
          : 'bg-cyan-500/20'
      }`}>
        <Play className={`w-4 h-4 ${isCompleted ? 'text-emerald-400' : 'text-cyan-400'}`} />
      </div>
    </div>
  </button>
)

export const TourSelectionModal: React.FC<TourSelectionModalProps> = ({ isOpen, onClose }) => {
  const { preferences } = usePlayerPreferences()
  const { startTour, closeTourSelection } = useTourGuide()

  // AGC: Start a specific tour
  const handleStartTour = (tourId: TourId) => {
    // AGC: Blockchain tour opens slides instead of regular tour
    if (tourId === 'blockchain') {
      onClose()
      closeTourSelection()
      // AGC: Dispatch event to open blockchain tour slides (rendered at App level)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('blockchain-tour:open'))
      }, 300)
      return
    }

    onClose()
    closeTourSelection()
    // AGC: Small delay to allow modal to close before tour starts
    setTimeout(() => {
      startTour(tourId)
    }, 300)
  }

  // AGC: Start battle tour from step 16 (Simulation tour)
  const handleStartSimulationTour = () => {
    onClose()
    closeTourSelection()
    // AGC: Small delay to allow modal to close before tour starts
    setTimeout(() => {
      // AGC: Start battle tour directly from step 16 (index 15)
      // AGC: The startTour function now accepts initialStepIndex parameter
      startTour('battle', 15) // AGC: Step 16 is index 15
    }, 300)
  }

  // AGC: Check if a tour is completed
  const isTourCompleted = (tourId: TourId): boolean => {
    const completedTours = preferences.completedTours ?? []
    return completedTours.includes(tourId)
  }

  // AGC: Count completed tours
  const completedTours = preferences.completedTours ?? []
  const completedCount = completedTours.length
  const totalCount = allTours.length

  if (!isOpen) {
    return null
  }

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* AGC: Backdrop with blur */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* AGC: Modal content */}
          <div className="relative w-full max-w-md bg-gradient-to-b from-slate-900 to-slate-950 border border-cyan-500/30 rounded-3xl shadow-2xl shadow-cyan-500/10 overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* AGC: Decorative gradient top */}
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-cyan-500/10 via-purple-500/10 to-transparent pointer-events-none" />
            
            {/* AGC: Close button */}
            <button
              onClick={() => {
                playClickSound()
                onClose()
              }}
              className="absolute top-4 right-4 p-2 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            
            {/* AGC: Header */}
            <div className="relative pt-6 pb-4 px-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <h2 className="text-xl font-bold text-white">
                  Choose a Tour
                </h2>
              </div>
              
              <p className="text-sm text-slate-400">
                {completedCount === totalCount 
                  ? 'All tours completed! Replay any tour below.'
                  : `${completedCount} of ${totalCount} tours completed`
                }
              </p>
              
              {/* AGC: Progress bar */}
              <div className="mt-3 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-all duration-500"
                  style={{ width: `${(completedCount / totalCount) * 100}%` }}
                />
              </div>
            </div>
            
            {/* AGC: Tour list */}
            <div className="px-6 pb-6">
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                {allTours.map((tour) => (
                  <TourCard
                    key={tour.id}
                    id={tour.id}
                    title={tour.title}
                    description={tour.description}
                    icon={tour.icon}
                    estimatedTime={tour.estimatedTime}
                    isCompleted={isTourCompleted(tour.id)}
                    onStart={() => handleStartTour(tour.id)}
                  />
                ))}
                
                {/* AGC: Simulation Tour - Start battle tour from step 16 */}
                <button
                  onClick={() => {
                    playClickSound()
                    handleStartSimulationTour()
                  }}
                  className="w-full p-4 rounded-xl transition-all group text-left bg-gradient-to-br from-green-600/20 to-emerald-600/20 hover:from-green-600/30 hover:to-emerald-600/30 border border-green-500/30 hover:border-green-400/50"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform bg-gradient-to-br from-green-500/20 to-emerald-500/20">
                      🎮
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-base transition-colors text-green-300 group-hover:text-green-200">
                          Simulation Tour
                        </h4>
                      </div>
                      <p className="text-sm text-slate-400 mt-0.5">
                        Start battle tour from simulation setup
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-slate-500">
                          ~3 min
                        </span>
                      </div>
                    </div>
                    <div className="p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity bg-green-500/20">
                      <Play className="w-4 h-4 text-green-400" />
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* AGC: Decorative corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-12 h-12 border-r-2 border-b-2 border-purple-500/30 rounded-br-3xl pointer-events-none" />
          </div>
        </div>
      )}

    </>
  )
}

export default TourSelectionModal

